import { useCallback, useEffect, useRef, useState } from 'react';

import { activateRateItReferral, resolveDeepLink } from 'services/tiri';
import { RATE_IT_DEFAULT_REFERRAL_POINTS, RATE_IT_REFERRAL_TYPE } from '../constants';

/**
 * The referral context behind a landing: who sent the link, and what each side earns.
 *
 * Both values come from ONE public call — `GET /api/deeplink/:shortCode`, which carries `sender`
 * and `referral.points` for Rate It links. That is why the landing URL's `?ref=` holds the
 * link's shortCode rather than the sender's id: the id alone would name the referrer but leave
 * the points figure unreachable, since every settings endpoint that carries it requires auth and
 * the banner's whole audience is signed-out.
 *
 * ACTIVATION is separate and authed, and fires only once the visitor has an identity. A guest
 * sees the banner immediately and is attributed later, on sign-in — which is why `activate` is
 * exposed for the auth step to call rather than run from here unconditionally.
 */
export default function useRateItReferral({ shortCode, currentUserId }) {
  const [sender, setSender] = useState(null);
  const [points, setPoints] = useState(RATE_IT_DEFAULT_REFERRAL_POINTS);
  const [isLoading, setIsLoading] = useState(!!shortCode);
  const [progress, setProgress] = useState(null);
  /**
   * The whole activate response, kept rather than picked apart.
   *
   * The `status` used to be discarded here, and that WAS bug 2: a fan reopening a referral they
   * had already finished got the ordinary feed and kept rating for a reward that could not come
   * again, with nothing on screen saying so. The landing branches on this.
   */
  const [activation, setActivation] = useState(null);
  /**
   * Whether activation has answered — either way. The landing holds its spinner on this, so the
   * feed cannot paint (and a rating cannot land) in the window before the block is known about.
   */
  const [activationResolved, setActivationResolved] = useState(false);

  // Resolving counts a click server-side, so this must run once per landing and not per render.
  const resolvedRef = useRef(null);
  // Activation is idempotent server-side, but re-posting on every render would be noise.
  const activatedRef = useRef(false);

  /**
   * Resolve once per link, and KEEP the answer even if the effect is torn down.
   *
   * There is deliberately no `cancelled` flag here, and adding one back deadlocks the banner.
   * The ref above already guarantees one request per shortCode, so a cancellation flag would be
   * a second guard on the same thing — and under React 18 StrictMode the two combine badly:
   * the first run claims the ref and starts the request, the simulated unmount sets `cancelled`,
   * the second run sees the ref already claimed and starts nothing, and the in-flight response
   * is then thrown away. `isLoading` never clears, so the banner shows its name skeleton
   * forever while `activate` quietly fills in the points beside it — which is exactly the state
   * this was found in.
   *
   * Discarding the result would be wrong even without StrictMode: the component instance and
   * its state survive, so the answer is still wanted by the same banner that asked for it.
   */
  useEffect(() => {
    if (!shortCode || resolvedRef.current === shortCode) return;
    resolvedRef.current = shortCode;
    setIsLoading(true);

    resolveDeepLink(shortCode).then((res) => {
      // A link that resolves to something else entirely — or not at all — simply has no banner.
      // The feed behind it is unaffected, so this is not an error state.
      if (res?.referral?.type === RATE_IT_REFERRAL_TYPE) {
        if (typeof res.referral.points === 'number') setPoints(res.referral.points);
        setSender(res.sender ?? null);
      }
      setIsLoading(false);
    });
  }, [shortCode]);

  /**
   * Attributes the signed-in fan as the referee.
   *
   * Safe to call more than once — the server is idempotent and reports an existing referral
   * rather than creating a second — but guarded anyway so a re-render does not post repeatedly.
   * The response's referrer wins over the link's: for a fan who was already referred by someone
   * else, the STORED referrer is the one whose reward is actually at stake.
   */
  const activate = useCallback(async () => {
    if (!shortCode || !currentUserId || activatedRef.current) return null;
    activatedRef.current = true;

    const res = await activateRateItReferral({ shortCode });

    // Resolved on the FAILURE path too. The landing gates its spinner on this, so an endpoint
    // that 500s would otherwise leave a fan staring at it forever rather than at the feed —
    // which is the wrong way to fail, since a referral we could not read still leaves the
    // moments perfectly rateable.
    setActivationResolved(true);

    if (!res || res.err) return null;

    setActivation(res);
    if (typeof res.points === 'number') setPoints(res.points);
    if (res.referrer) setSender(res.referrer);
    if (res.progress) setProgress(res.progress);

    return res;
  }, [shortCode, currentUserId]);

  return {
    /** Null when there is no referral context — the banner simply does not render. */
    sender,
    points,
    isLoading,
    progress,
    activate,
    activationResolved,
    /**
     * This link's referral is finished. Per LINK, not per fan: a fan who completed one invite
     * and was sent another gets this for the first and an ordinary feed for the second.
     */
    isCompletedLink: activation?.status === 'completed',
    /**
     * What this fan was actually paid on this link. Deliberately not defaulted to 0 — the server
     * omits it on a referral that completed before the figure was recorded, and "we have no
     * record" is a different sentence from "you earned nothing".
     */
    pointsAwarded: activation?.pointsAwarded,
    /** A fan following their own link gets no banner; it would promise them nothing. */
    isSelfReferral: !!sender && !!currentUserId && sender.id === currentUserId
  };
}
